import { trait } from '@traits-ts/core'
import type { Task, TaskPriority, TaskStatus, TaskType } from '../../types/task.js'

/**
 * BaseTaskTrait provides the foundation behaviors for all tasks
 */
export const AsBaseTask = trait(superclass => class extends superclass implements Task {
  id = ''
  title = ''
  description = ''
  taskType: TaskType = 'system'
  priority: TaskPriority = 'medium'
  status: TaskStatus = 'pending'
  createdAt = new Date()
  updatedAt = new Date()
  taskImplementation = 'BaseTask'
  traits: string[] = []
  metadata: Record<string, unknown> = {}
  dueDate?: Date
  completedAt?: Date

  constructor(taskImplementationName: string) {
    super()

    // Use the explicitly provided task implementation name if available
    if (taskImplementationName) {
      this.taskImplementation = taskImplementationName
    }
  }

  /**
   * Execute the task - should be overridden by subclasses
   * @returns Promise resolving to true if execution was successful
   */
  async execute(): Promise<boolean> {
    return true
  }

  /**
   * Validate if the task can be executed
   * @returns Promise resolving to true if task is valid and can be executed
   */
  async validate(): Promise<boolean> {
    return true
  }

  /**
   * Called when a task is completed successfully
   */
  async onComplete(): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Called when a task fails
   */
  async onFail(): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Update the task status
   * @param status New status to set
   */
  updateStatus(status: TaskStatus): void {
    this.status = status
    this.updatedAt = new Date()

    if (status === 'completed') {
      this.completedAt = new Date()
    }
  }

  /**
   * Handle changes to referenced entities
   * Updates task metadata and title when member or subkrewe names change
   */
  async onReferencedEntityChanged(
    entityId: string,
    entityType: string,
    changes: Record<string, unknown>,
  ): Promise<boolean> {
    let updated = false

    // Handle member name changes
    if (entityType === 'member'
      && entityId === this.metadata.memberId
      && changes.displayName) {
      this.metadata.memberName = changes.displayName
      updated = true
    }

    // Handle subkrewe name changes
    if (entityType === 'subkrewe'
      && entityId === this.metadata.subkreweId
      && changes.name) {
      this.metadata.subkreweName = changes.name
      updated = true
    }

    // Update title and description if needed
    if (updated) {
      this.title = `Request to join ${this.metadata.subkreweName || 'SubKrewe'} from ${this.metadata.memberName || 'a member'}`
      this.description = `${this.metadata.memberName || 'A member'} has requested to join ${this.metadata.subkreweName || 'this SubKrewe'}.`
      this.updatedAt = new Date()
    }

    return updated
  }
})
